import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { X, Send, MessageCircle, Phone, FileText } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { DEFAULT_TEMPLATE, buildWhatsAppUrl, resolveTemplate } from "../lib/whatsapp";
import { log } from "../lib/logger";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BROCHURE_LINE_RE = /\n?Brochure:\s*https?:\/\/\S+\s*$/i;

/**
 * WhatsApp share dialog:
 *   - Pick a contact from the lead
 *   - Optionally tweak the intro message (prefilled from the FE's saved template)
 *   - Opens wa.me in a new tab and (best-effort) fetches a fresh copy of the user so
 *     any template change made from the Settings page in another tab is reflected.
 */
export default function WhatsAppShareDialog({ lead, onClose }) {
  const { user, setUser } = useAuth();
  const contacts = lead?.contacts || [];
  // Default contact: first "owner", else first available.
  const defaultContactIdx = useMemo(() => {
    const ownerIdx = contacts.findIndex((c) => (c.role || "").toLowerCase() === "owner");
    return ownerIdx >= 0 ? ownerIdx : 0;
  }, [contacts]);
  const [contactIdx, setContactIdx] = useState(defaultContactIdx);
  const contact = contacts[contactIdx];

  const templateSource = user?.whatsapp_template || DEFAULT_TEMPLATE;
  const [message, setMessage] = useState(() => resolveTemplate(templateSource, { lead, contact: contacts[defaultContactIdx], user }));
  const [refreshed, setRefreshed] = useState(false);
  const [brochure, setBrochure] = useState(null); // {exists, url_path, ...}

  // Fetch the current brochure state once. If a brochure is set, append it as a Brochure: link
  // at the bottom of the message. Manual edits by the user are respected — we only inject once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/brochure");
        if (cancelled) return;
        setBrochure(r.data);
        if (r.data?.exists) {
          const url = `${API_URL}${r.data.url_path}`;
          setMessage((m) => (BROCHURE_LINE_RE.test(m) ? m : `${m}\n\nBrochure: ${url}`));
        }
      } catch (e) { log.warn("[whatsapp] brochure fetch skipped (none set or network hiccup)", e); }
    })();
    return () => { cancelled = true; };
  }, []);

  const brochureSuffix = brochure?.exists ? `\n\nBrochure: ${API_URL}${brochure.url_path}` : "";

  // If the user updated their template on another tab / device, fetch it once when the dialog opens.
  const refreshTemplate = async () => {
    try {
      const r = await api.get("/auth/me");
      if (r.data) {
        setUser(r.data);
        localStorage.setItem("sohom_user", JSON.stringify(r.data));
        const base = resolveTemplate(r.data.whatsapp_template || DEFAULT_TEMPLATE, { lead, contact, user: r.data });
        setMessage(base + brochureSuffix);
        setRefreshed(true);
      }
    } catch (err) {
      // Non-fatal — the current cached template still works fine.
      console.warn("[Sohom] Could not refresh WhatsApp template:", err?.message || err);
    }
  };

  const applyContact = (idx) => {
    setContactIdx(idx);
    const base = resolveTemplate(templateSource, { lead, contact: contacts[idx], user });
    setMessage(base + brochureSuffix);
  };

  const canSend = !!(contact && contact.phone && message.trim());
  const waUrl = canSend ? buildWhatsAppUrl(contact.phone, message) : "";

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="whatsapp-dialog">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 shrink-0">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900" style={{fontFamily:"'Outfit', sans-serif"}}>Send via WhatsApp</h2>
              <p className="text-xs text-slate-500 mt-0.5">Pick a contact and review the message before sending.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" data-testid="whatsapp-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Contact picker */}
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Send to <span className="text-red-500">*</span></label>
            {contacts.length === 0 ? (
              <div className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="whatsapp-no-contacts">
                This lead has no contacts yet. Add a contact on the Lead Detail page first.
              </div>
            ) : (
              <div className="mt-1 space-y-1.5" data-testid="whatsapp-contact-list">
                {contacts.map((c, i) => {
                  const disabled = !c.phone;
                  return (
                    <button
                      key={`${c.role}-${c.name}-${c.phone}-${i}`}
                      type="button"
                      disabled={disabled}
                      onClick={() => applyContact(i)}
                      data-testid={`whatsapp-contact-${i}`}
                      className={`w-full text-left rounded-md border px-3 py-2 flex items-center justify-between gap-3 transition-colors ${
                        disabled
                          ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                          : contactIdx === i
                            ? "border-emerald-500 bg-emerald-50"
                            : "border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {c.name || "(no name)"} <span className="text-xs text-slate-500 ml-1">({(c.role || "other").replace("_", " ")})</span>
                        </div>
                        <div className="text-xs text-slate-500 inline-flex items-center gap-1 mt-0.5">
                          <Phone className="w-3 h-3" />
                          {c.phone || <span className="italic">no phone on file</span>}
                        </div>
                      </div>
                      {contactIdx === i && !disabled && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Selected</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Editable message */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Message</label>
              <div className="flex items-center gap-2">
                {!refreshed && (
                  <button type="button" onClick={refreshTemplate} className="text-[11px] text-slate-500 hover:text-slate-800 underline" data-testid="whatsapp-refresh-template">
                    Refresh my template
                  </button>
                )}
                <Link
                  to="/settings"
                  onClick={onClose}
                  className="text-[11px] text-sky-600 hover:text-sky-800 underline"
                  data-testid="whatsapp-open-settings"
                >
                  Edit template →
                </Link>
              </div>
            </div>
            <textarea
              className="sohom-input mt-1"
              rows={7}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="whatsapp-message"
            />
            <div className="text-[11px] text-slate-400 mt-1">
              {message.length} characters · WhatsApp attachments aren&apos;t supported via link — paste any brochure URL here if needed.
            </div>
            {brochure?.exists && (
              <div className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 inline-flex items-center gap-1.5" data-testid="whatsapp-brochure-indicator">
                <FileText className="w-3 h-3" />
                Brochure auto-appended: <span className="font-medium">{brochure.original_filename}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="sohom-btn-secondary" data-testid="whatsapp-cancel">Cancel</button>
          <a
            href={canSend ? waUrl : undefined}
            aria-disabled={!canSend}
            onClick={(e) => {
              if (!canSend) e.preventDefault();
              else setTimeout(onClose, 300);
            }}
            target="_blank"
            rel="noreferrer"
            className={`sohom-btn-primary bg-emerald-600 hover:bg-emerald-700 ${canSend ? "" : "opacity-50 cursor-not-allowed"}`}
            data-testid="whatsapp-send"
          >
            <Send className="w-4 h-4" />
            Open WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}
